import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Send, Users, BellRing, Archive, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  getCompanyPolicies,
  createCompanyPolicy,
  addPolicyVersion,
  publishCompanyPolicy,
  archiveCompanyPolicy,
  remindPolicy,
  getPolicyAcknowledgements
} from '../../services/api/implementation/impl';
import {
  StatusPill,
  Tile,
  toneForStatus,
  Callout
} from '../../../shared/components/ui/primitives';

/**
 * The policy library.
 *
 * A published wording is never edited — every change is a new version, because
 * an acknowledgement points at a version and editing under it would leave a
 * record of somebody accepting text they never saw. The UI offers "new version"
 * rather than "edit" for exactly that reason, and says so.
 */

const CATEGORIES = [
  'CODE_OF_CONDUCT', 'HR', 'SAFETY', 'FINANCE', 'IT',
  'LEAVE', 'COMPLIANCE', 'SITE_OPERATIONS', 'OTHER'
];

const AUDIENCES = [
  { value: 'ALL_EMPLOYEES', label: 'Everyone' },
  { value: 'VERTICAL', label: 'Certain departments' },
  { value: 'DESIGNATION', label: 'Certain roles' },
  { value: 'SPECIFIC_USERS', label: 'Named people' }
];

const Policies = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=employees' });
  const queryClient = useQueryClient();
  const [editor, setEditor] = useState(null);
  const [chasing, setChasing] = useState(null);

  const { data, isFetching } = useQuery({
    queryKey: ['companyPolicies'],
    queryFn: () => getCompanyPolicies()
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['companyPolicies'] });

  const save = useMutation({
    mutationFn: ({ policyId, ...payload }) =>
      policyId ? addPolicyVersion(policyId, payload) : createCompanyPolicy(payload),
    onSuccess: (_r, variables) => {
      refresh();
      setEditor(null);
      toast.success(
        variables.policyId
          ? 'New draft version added — publish it to circulate'
          : 'Draft created — publish it to circulate'
      );
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not save')
  });

  const publish = useMutation({
    mutationFn: (policyId) => publishCompanyPolicy(policyId),
    onSuccess: (result) => {
      refresh();
      toast.success(result?.message || 'Published');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not publish')
  });

  const remind = useMutation({
    mutationFn: (policyId) => remindPolicy(policyId),
    onSuccess: (result) => toast.success(result?.message || 'Reminded'),
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not send reminders')
  });

  const archive = useMutation({
    mutationFn: (policyId) => archiveCompanyPolicy(policyId),
    onSuccess: () => {
      refresh();
      toast.success('Archived. Past acknowledgements are unaffected.');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not archive')
  });

  const rows = data?.data ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Company Policies' }} />

      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <Callout tone="info" collapsible icon={<Info size={14} />} title="How policy versions work" className="min-w-0 flex-1 sm:max-w-2xl">
            A published policy is never edited. Changing it creates a new version, and everyone who accepted the old one is asked again.
          </Callout>
          <button
            type="button"
            onClick={() => setEditor({ mode: 'create' })}
            className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white"
          >
            <Plus size={15} /> New policy
          </button>
        </div>

        {isFetching ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-10 text-center">
            <FileText size={28} className="mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              No policies yet. Nothing is pre-loaded — a policy is your wording,
              not a template.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((policy) => (
              <div key={policy.id} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{policy.title}</span>
                      <PolicyPill status={policy.status} />
                      {policy.requires_acknowledgement && (
                        <span className="rounded bg-warning-bg px-1.5 py-0.5 text-[10px] font-semibold uppercase text-warning-fg">
                          Acknowledgement required
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {titleCase(policy.category)} · {audienceLabel(policy)}
                      {policy.current_version
                        ? ` · v${policy.current_version.version_no} published ${shortDate(
                            policy.current_version.published_at
                          )}`
                        : ' · never published'}
                    </div>
                    {policy.summary && (
                      <p className="mt-1 text-sm text-gray-600">{policy.summary}</p>
                    )}

                    {policy.draft_version && (
                      <div className="mt-2 rounded border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
                        <strong>v{policy.draft_version.version_no} is an unpublished draft.</strong>{' '}
                        {policy.draft_version.change_note}
                      </div>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-wrap gap-2">
                    {policy.draft_version && (
                      <button
                        type="button"
                        disabled={publish.isPending}
                        onClick={() => publish.mutate(policy.id)}
                        className="flex items-center gap-1 rounded bg-brand-700 px-2.5 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                      >
                        <Send size={12} /> Publish v{policy.draft_version.version_no}
                      </button>
                    )}
                    {!policy.draft_version && policy.status !== 'ARCHIVED' && (
                      <button
                        type="button"
                        onClick={() => setEditor({ mode: 'version', policy })}
                        className="rounded border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700"
                      >
                        New version
                      </button>
                    )}
                    {policy.current_version && (
                      <button
                        type="button"
                        onClick={() => setChasing(policy)}
                        className="flex items-center gap-1 rounded border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700"
                      >
                        <Users size={12} /> {policy.acknowledged_count} accepted
                      </button>
                    )}
                    {policy.requires_acknowledgement && policy.current_version && (
                      <button
                        type="button"
                        disabled={remind.isPending}
                        onClick={() => remind.mutate(policy.id)}
                        className="flex items-center gap-1 rounded border border-warning-border px-2.5 py-1.5 text-xs text-warning-fg disabled:opacity-50"
                      >
                        <BellRing size={12} /> Chase
                      </button>
                    )}
                    {policy.status !== 'ARCHIVED' && (
                      <button
                        type="button"
                        onClick={() => {
                          // eslint-disable-next-line no-alert
                          if (window.confirm('Archive this policy? Past acknowledgements are kept.')) {
                            archive.mutate(policy.id);
                          }
                        }}
                        className="rounded border border-gray-300 px-2 py-1.5 text-xs text-gray-500"
                        aria-label="Archive"
                      >
                        <Archive size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editor && (
        <PolicyEditor
          mode={editor.mode}
          policy={editor.policy}
          busy={save.isPending}
          onClose={() => setEditor(null)}
          onSubmit={(values) =>
            save.mutate(
              editor.mode === 'version'
                ? { policyId: editor.policy.id, ...values }
                : values
            )
          }
        />
      )}

      {chasing && <ChaseModal policy={chasing} onClose={() => setChasing(null)} />}
    </Layout>
  );
};

const PolicyEditor = ({ mode, policy, busy, onClose, onSubmit }) => {
  const isVersion = mode === 'version';
  const [form, setForm] = useState({
    title: '',
    category: 'HR',
    summary: '',
    body: '',
    requires_acknowledgement: false,
    audience: 'ALL_EMPLOYEES',
    audience_verticals: '',
    audience_designations: '',
    audience_user_ids: '',
    change_note: '',
    effective_from: ''
  });
  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
    }));

  const narrowingField = {
    VERTICAL: 'audience_verticals',
    DESIGNATION: 'audience_designations',
    SPECIFIC_USERS: 'audience_user_ids'
  }[form.audience];

  return (
    <Modal
      name={isVersion ? `New version of "${policy.title}"` : 'New policy'}
      onClose={onClose}
      width="640px"
    >
      <div className="space-y-3">
        {isVersion && (
          <p className="rounded bg-warning-bg px-3 py-2 text-xs text-warning-fg">
            This creates version {(policy.version_count ?? 0) + 1}. Once
            published, everybody in the audience is asked to read it again — the
            old wording stays on file with the acknowledgements it collected.
          </p>
        )}

        {!isVersion && (
          <>
            <Field label="Title">
              <input
                value={form.title}
                onChange={set('title')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="Code of Conduct"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <select
                  value={form.category}
                  onChange={set('category')}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{titleCase(c)}</option>
                  ))}
                </select>
              </Field>
              <Field label="Who is it for">
                <select
                  value={form.audience}
                  onChange={set('audience')}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  {AUDIENCES.map((a) => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </Field>
            </div>

            {narrowingField && (
              <Field label={AUDIENCES.find((a) => a.value === form.audience)?.label}>
                <input
                  value={form[narrowingField]}
                  onChange={set(narrowingField)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder={
                    form.audience === 'SPECIFIC_USERS'
                      ? 'User ids, comma separated'
                      : 'Comma separated, e.g. Operations, Accounts'
                  }
                />
                {/* An empty list resolves to nobody server-side, not everybody.
                    Saying so here saves a policy that looks published and
                    reached no one. */}
                <p className="mt-1 text-xs text-gray-500">
                  Leave this empty and the policy reaches nobody.
                </p>
              </Field>
            )}

            <Field label="Summary">
              <input
                value={form.summary}
                onChange={set('summary')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="One line, shown in the notification"
              />
            </Field>

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.requires_acknowledgement}
                onChange={set('requires_acknowledgement')}
                className="mt-0.5 h-4 w-4 accent-brand-700"
              />
              <span>
                Require acknowledgement
                <span className="block text-xs text-gray-500">
                  Generates a chase list. Only useful while it is short — leave
                  it off for a notice.
                </span>
              </span>
            </label>
          </>
        )}

        <Field label={isVersion ? 'Revised text' : 'Policy text'}>
          <textarea
            value={form.body}
            onChange={set('body')}
            rows={8}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>

        {isVersion && (
          <Field label="What changed">
            <input
              value={form.change_note}
              onChange={set('change_note')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Notice period increased to 60 days"
            />
            <p className="mt-1 text-xs text-gray-500">
              Shown to everybody being asked to accept it again. Without it,
              re-acknowledgement becomes a reflex click.
            </p>
          </Field>
        )}

        <Field label="Effective from">
          <input
            type="date"
            value={form.effective_from}
            onChange={set('effective_from')}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (!isVersion && !form.title.trim()) {
                toast.error('Give the policy a title');
                return;
              }
              if (!form.body.trim()) {
                toast.error('A policy needs some text');
                return;
              }
              if (isVersion && !form.change_note.trim()) {
                toast.error('Say what changed');
                return;
              }
              onSubmit({
                ...form,
                effective_from: form.effective_from || null
              });
            }}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Save as draft'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Who has and has not accepted.
 *
 * Both lists, because a count answers "are we compliant" and only the names let
 * somebody do anything about it.
 */
const ChaseModal = ({ policy, onClose }) => {
  const { data, isFetching } = useQuery({
    queryKey: ['policyAcknowledgements', policy.id],
    queryFn: () => getPolicyAcknowledgements(policy.id)
  });

  const acknowledged = data?.acknowledged ?? [];
  const outstanding = data?.outstanding ?? [];

  return (
    <Modal name={`Acknowledgements — ${policy.title}`} onClose={onClose} width="560px">
      {isFetching ? (
        <Loader />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Tile variant="card" label="Accepted" value={acknowledged.length} tone="success" />
            <Tile variant="card" label="Outstanding" value={outstanding.length} tone="warning" />
          </div>

          {outstanding.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium text-gray-700">Still to accept</div>
              <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
                {outstanding.map((person) => (
                  <li key={person.user_id} className="flex justify-between rounded bg-warning-bg px-2 py-1">
                    <span>{person.name ?? `User ${person.user_id}`}</span>
                    <span className="text-xs text-gray-500">
                      {[person.designation, person.vertical].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {acknowledged.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium text-gray-700">Accepted</div>
              <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
                {acknowledged.map((person) => (
                  <li key={person.user_id} className="flex justify-between px-2 py-1">
                    <span>{person.name ?? `User ${person.user_id}`}</span>
                    <span className="text-xs text-gray-500">
                      {shortDate(person.acknowledged_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

/** A policy is published, archived, or still a draft. */
const PolicyPill = ({ status }) => (
  <StatusPill dense uppercase tone={toneForStatus(status === 'PUBLISHED' ? 'PUBLISHED' : status === 'ARCHIVED' ? 'ARCHIVED' : 'DRAFT')}>
    {titleCase(status)}
  </StatusPill>
);

const Field = ({ label, children }) => (
  <div>
    <div className="mb-1 text-xs font-medium text-gray-600">{label}</div>
    {children}
  </div>
);

function audienceLabel(policy) {
  if (policy.audience === 'ALL_EMPLOYEES') return 'Everyone';
  const list =
    policy.audience_verticals ?? policy.audience_designations ?? policy.audience_user_ids;
  return list ? `${titleCase(policy.audience)}: ${list}` : `${titleCase(policy.audience)} (nobody)`;
}

function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function titleCase(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default Policies;
